import { useQuery } from '@tanstack/vue-query';
import { api, type TimeEntryResponse, type TimeEntry } from '@/packages/api/src';
import { getCurrentMembershipId, getCurrentOrganizationId } from '@/utils/useUser';
import { computed, type Ref } from 'vue';
import type { Dayjs } from 'dayjs';
import { getDayJsInstance, localDateToUtc } from '@/packages/ui/src/utils/time';
import { getWeekStartDayNumber } from '@/packages/ui/src/utils/settings';
import {
    nextVisibleDay,
    previousVisibleDay,
} from '@/packages/ui/src/FullCalendar/useCalendarNavigation';

/**
 * Calculate expanded date range to include previous and next periods with timezone transformations.
 * This allows smooth navigation between calendar views without loading delays.
 */
export function getExpandedCalendarDateRange(
    calendarStart: Dayjs,
    calendarEnd: Dayjs
): { start: string; end: string } {
    const duration = calendarEnd.diff(calendarStart, 'milliseconds');

    // Calculate previous period
    const previousStart = calendarStart.subtract(duration, 'milliseconds');
    // Calculate next period
    const nextEnd = calendarEnd.add(duration, 'milliseconds');

    return {
        start: localDateToUtc(previousStart),
        end: localDateToUtc(nextEnd),
    };
}

/**
 * Get the initial week view date range based on user's week start preference.
 * Matches FullCalendar's timeGridWeek initial view.
 */
export function getInitialWeekRange(): { start: Dayjs; end: Dayjs } {
    const dayjs = getDayJsInstance();
    const firstDay = getWeekStartDayNumber();

    const now = dayjs();
    const currentDayOfWeek = now.day();
    const daysFromWeekStart = (currentDayOfWeek - firstDay + 7) % 7;
    const start = now.subtract(daysFromWeekStart, 'day').startOf('day');
    const end = start.add(7, 'day');

    return { start, end };
}

/**
 * Same week range as getInitialWeekRange, but trimmed to the days the calendar
 * actually renders (le week-end est masqué). Sans ça, le préchargement calcule
 * une plage lundi->lundi alors que le calendrier demande lundi->samedi : les
 * clés de cache diffèrent et le préchargement est perdu.
 */
export function getInitialCalendarWeekRange(): { start: Dayjs; end: Dayjs } {
    const { start, end } = getInitialWeekRange();
    const visibleStart = nextVisibleDay(start);
    // end est exclusif : on repart du dernier jour inclus.
    const visibleEnd = previousVisibleDay(end.subtract(1, 'day')).add(1, 'day');
    return { start: visibleStart, end: visibleEnd };
}

/**
 * Create the query key for calendar time entries.
 *
 * memberId fait partie de la clé : sans lui, passer d'un membre à l'autre
 * dans le sélecteur servirait les entrées mises en cache du membre précédent.
 */
export function createCalendarQueryKey(
    start: string | null,
    end: string | null,
    organizationId: string | null,
    memberId?: string
): readonly [
    'timeEntries',
    'calendar',
    {
        start: string | null;
        end: string | null;
        organization: string | null;
        member: string | null;
    },
] {
    return [
        'timeEntries',
        'calendar',
        { start, end, organization: organizationId, member: memberId ?? null },
    ] as const;
}

/**
 * Fetch all calendar entries with pagination.
 */
export async function fetchAllCalendarEntries(
    organizationId: string,
    memberId: string | undefined,
    start: string,
    end: string
): Promise<TimeEntryResponse> {
    const allEntries: TimeEntry[] = [];

    while (true) {
        const response = await api.getTimeEntries({
            params: {
                organization: organizationId,
            },
            queries: {
                start,
                end,
                member_id: memberId,
                offset: allEntries.length || undefined,
            },
        });

        if (response.data.length === 0) {
            return { data: allEntries, meta: response.meta };
        }

        allEntries.push(...response.data);

        if (allEntries.length >= response.meta.total) {
            return { data: allEntries, meta: response.meta };
        }
    }
}

export function useTimeEntriesCalendarQuery(
    calendarStart: Ref<Dayjs | undefined>,
    calendarEnd: Ref<Dayjs | undefined>,
    // Membre dont on affiche le calendrier. Non fourni (ou undefined) = le membre
    // connecté. Un autre membre exige la permission API time-entries:view:all.
    memberId?: Ref<string | undefined>
) {
    const effectiveMemberId = computed(() => memberId?.value ?? getCurrentMembershipId());

    const enableCalendarQuery = computed(() => {
        return !!getCurrentOrganizationId() && !!calendarStart.value && !!calendarEnd.value;
    });

    const expandedDateRange = computed(() => {
        if (!calendarStart.value || !calendarEnd.value) {
            return { start: null, end: null };
        }
        return getExpandedCalendarDateRange(calendarStart.value, calendarEnd.value);
    });

    return useQuery<TimeEntryResponse>({
        queryKey: computed(() =>
            createCalendarQueryKey(
                expandedDateRange.value.start,
                expandedDateRange.value.end,
                getCurrentOrganizationId(),
                effectiveMemberId.value
            )
        ),
        enabled: enableCalendarQuery,
        placeholderData: (previousData) => previousData,
        queryFn: async () => {
            return fetchAllCalendarEntries(
                getCurrentOrganizationId() || '',
                effectiveMemberId.value,
                expandedDateRange.value.start!,
                expandedDateRange.value.end!
            );
        },
        staleTime: 1000 * 30, // 30 seconds
    });
}
